import React, { useEffect, useState } from "react";
import "../Zone/Zone.css";
import "../Overview/Overview.css";
import { fetchComplaintsPage, fetchComplaintSourceCounts } from "../../Services/locationEntries";
import ComplaintFilters from "../Overview/components/ComplaintFilters";
import ComplaintRow from "../Overview/components/ComplaintRow";

// Matches HomeComplaints so the two complaint lists page identically.
const PAGE_SIZE = 20;

function LocationComplaints({ className, location, zone, ward, range, onBack, onComplaintClick }) {
  const [entries, setEntries] = useState(null);
  const [cursors, setCursors] = useState(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCounts, setTotalCounts] = useState(null);
  const [filters, setFilters] = useState({ source: "", status: "" });
  const { source, status } = filters;
  const from = range?.from;
  const to = range?.to;

  useEffect(() => {
    if (!location?.documentId) return;
    setEntries(null);
    setCursors(undefined);
    setHasMore(false);
    fetchComplaintsPage({ locationId: location.documentId, from, to, source, status, pageSize: PAGE_SIZE })
      .then((page) => {
        setEntries(page.items);
        setCursors(page.cursors);
        setHasMore(page.hasMore);
      })
      .catch(() => {
        setEntries([]);
        setHasMore(false);
      });
  }, [location?.documentId, from, to, source, status]);

  // Unfiltered total for this location, so the header pill reads "X of Y"
  // like HomeComplaints — independent of the source/status filters above.
  useEffect(() => {
    if (!location?.documentId) return;
    fetchComplaintSourceCounts({ locationId: location.documentId, from, to })
      .then(setTotalCounts)
      .catch(() => setTotalCounts({ total: 0 }));
  }, [location?.documentId, from, to]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    fetchComplaintsPage({ locationId: location.documentId, from, to, source, status, pageSize: PAGE_SIZE, cursors })
      .then((page) => {
        setEntries((prev) => [...(prev || []), ...page.items]);
        setCursors(page.cursors);
        setHasMore(page.hasMore);
      })
      .catch(() => setHasMore(false))
      .finally(() => setLoadingMore(false));
  };

  return (
    <div className={className} data-cy="location-complaints-screen">
      <div className="subhdr">
        <div className="back" onClick={onBack}>
          <svg viewBox="0 0 24 24">
            <polyline points="15,18 9,12 15,6" />
          </svg>
        </div>
        <div className="subhdr-info">
          <div className="subhdr-crumb">
            Chennai › Zone {zone?.id} › Ward {ward?.id} › {location?.location || location?.PCTNo}
          </div>
          <div className="subhdr-title">Complaints</div>
        </div>
        {entries && (
          <span className="pill neutral">
            {source || status ? entries.length : <>{entries.length} of {totalCounts?.total ?? 0}</>}
          </span>
        )}
      </div>
      <div className="divider" />
      <div className="filter-stack" style={{ marginTop: 0 }}>
        <ComplaintFilters source={source} status={status} onChange={setFilters} />
      </div>
      <div className="scroll">
        {entries === null && (
          <div style={{ padding: "24px 20px", fontSize: "var(--text-base)", color: "var(--t3)" }}>Loading…</div>
        )}
        {entries?.length === 0 && (
          <div style={{ padding: "24px 20px", fontSize: "var(--text-base)", color: "var(--t3)" }}>No complaints found.</div>
        )}
        {entries?.length > 0 && (
          <div className="lrows">
            {entries.map((c) => (
              <ComplaintRow key={c.documentId} c={c} onComplaintClick={onComplaintClick} />
            ))}
          </div>
        )}
        {hasMore && (
          <div
            data-cy="location-complaints-load-more"
            onClick={loadingMore ? undefined : handleLoadMore}
            style={{
              padding: "14px 20px", textAlign: "center", fontSize: "var(--text-base)",
              color: "var(--t2)", cursor: loadingMore ? "default" : "pointer",
            }}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </div>
        )}
        <div style={{ height: "20px" }} />
      </div>
    </div>
  );
}

export default LocationComplaints;
